import { cn } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../shadcn/avatar';

type SessionProps = {
  displayName: string | null;
  pictureUrl?: string | null;
};

/**
 * Two initials, not one. A single letter collides constantly in a customer
 * directory — every Ahmed, Ali and Amal renders an identical circle — and it
 * reads as a placeholder rather than as a person.
 *
 * Uses the FIRST and LAST name parts so middle names and patronymics (common in
 * Arabic names, which frequently run to four parts) do not push out the family
 * name. Falls back to the first two characters of a single-word name.
 *
 * `Array.from` rather than `slice`, so a name whose first character is outside
 * the BMP is not cut in half mid-surrogate-pair.
 */
function getInitials(displayName?: string | null) {
  const parts = displayName?.trim().split(/\s+/).filter(Boolean) ?? [];

  if (parts.length === 0) {
    return '';
  }

  if (parts.length === 1) {
    return Array.from(parts[0] as string)
      .slice(0, 2)
      .join('');
  }

  const first = Array.from(parts[0] as string)[0] ?? '';
  const last = Array.from(parts[parts.length - 1] as string)[0] ?? '';

  return `${first}${last}`;
}

type TextProps = {
  text: string;
};

type ProfileAvatarProps = (SessionProps | TextProps) & {
  className?: string;
  fallbackClassName?: string;
};

export function ProfileAvatar(props: ProfileAvatarProps) {
  // Defaults FIRST, caller's className LAST. cn() is tailwind-merge, so the
  // trailing argument wins on conflicting utilities — with the old order the
  // hardcoded `mx-auto h-9 w-9` beat anything a caller passed, making the size
  // and margin silently unoverridable (a 28px avatar in the top bar could not
  // be expressed at all).
  const avatarClassName = cn(
    'mx-auto h-9 w-9 group-focus:ring-2',
    props.className,
  );

  if ('text' in props) {
    return (
      <Avatar className={avatarClassName}>
        <AvatarFallback
          className={cn(
            props.fallbackClassName,
            'animate-in fade-in uppercase',
          )}
        >
          {props.text.slice(0, 1)}
        </AvatarFallback>
      </Avatar>
    );
  }

  const initials = getInitials(props.displayName);

  return (
    <Avatar className={avatarClassName}>
      <AvatarImage src={props.pictureUrl ?? undefined} />

      <AvatarFallback
        className={cn(props.fallbackClassName, 'animate-in fade-in')}
      >
        <span suppressHydrationWarning className={'uppercase'}>
          {initials}
        </span>
      </AvatarFallback>
    </Avatar>
  );
}
